import React, { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { FormControl, Select, MenuItem, Box, Typography } from "@mui/material";
import { Business as TenantIcon } from "@mui/icons-material";
import {
  useTenantsListDropdown,
  useTenantLogoUrl,
  useTenantLogoUrls,
} from "../../features/tenant-management/hooks/useTenants";
import { useTenant } from "../../contexts/TenantContext";
import { THEME_CONSTANTS } from "../../theme/constants";
import { topbarSelectSx } from "./topbarDropdownStyles";

interface TenantDropdownProps {
  /**
   * Whether the user is an admin.
   * This value comes from the login response via AuthContext.
   */
  isAdmin?: boolean;
  /** Render in topbar style — matches the search bar dimensions and DS tokens. */
  topbar?: boolean;
}

interface TenantAvatarProps {
  tenantId: number;
  hasLogo: boolean;
  name: string;
  logoUrl?: string;
}

const TenantAvatar: React.FC<TenantAvatarProps> = ({
  tenantId,
  hasLogo,
  name,
  logoUrl: preResolved,
}) => {
  const [imageError, setImageError] = useState(false);
  const { data: fetchedUrl } = useTenantLogoUrl(
    tenantId,
    hasLogo && !imageError && preResolved === undefined,
  );
  const logoUrl = preResolved ?? fetchedUrl;

  if (logoUrl && !imageError) {
    return (
      <Box
        component="img"
        src={logoUrl}
        alt={`${name} logo`}
        onError={() => setImageError(true)}
        sx={{
          width: 18,
          height: 18,
          borderRadius: THEME_CONSTANTS.borderRadius.round,
          objectFit: "contain",
          border: "none",
          boxShadow: "none",
          backgroundColor: "var(--bg-elev)",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: THEME_CONSTANTS.borderRadius.round,
        bgcolor: "var(--bg-sunken)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        boxShadow: "none",
      }}
    >
      <TenantIcon
        sx={{
          fontSize: 14,
          color: "var(--ink-3)",
        }}
      />
    </Box>
  );
};

/**
 * TenantDropdown Component
 *
 * Displays a dropdown in the sidebar that allows admins to switch between tenants.
 * Shows a list of tenants with "Global" as the default first option.
 *
 * IMPORTANT: This component is only visible when isAdmin is true.
 * The isAdmin value is provided from the login response via AuthContext.
 *
 * @see Sidebar.tsx for integration
 */
export const TenantDropdown: React.FC<TenantDropdownProps> = ({
  isAdmin = false,
  topbar = false,
}) => {
  const { selectedTenantId, setSelectedTenantId } = useTenant();

  // Fetch tenants using React Query hook
  const {
    data: tenants = [],
    isLoading: loading,
    isSuccess,
  } = useTenantsListDropdown({
    enabled: isAdmin,
  });

  // Batch-fetch all tenant logo URLs to avoid N+1 requests
  const logoInputs = useMemo(
    () => tenants.map((t) => ({ id: t.tenantId, hasLogo: !!t.logo })),
    [tenants],
  );
  const logoUrlMap = useTenantLogoUrls(logoInputs);

  // Validate selectedTenantId exists in the list, reset to Global if invalid
  useEffect(() => {
    if (isSuccess && tenants.length > 0) {
      setSelectedTenantId((currentTenantId) => {
        if (
          currentTenantId !== null &&
          !tenants.some((t) => t.tenantId === currentTenantId)
        ) {
          return null;
        }
        return currentTenantId;
      });
    }
  }, [isSuccess, tenants, setSelectedTenantId]);

  const handleTenantChange = (event: { target: { value: unknown } }) => {
    const value = event.target.value as string;
    const tenantId = value === "global" ? null : parseInt(value, 10);

    if (tenantId === null || !isNaN(tenantId)) {
      setSelectedTenantId(tenantId);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Box
      sx={
        topbar
          ? { width: "100%", display: "block" }
          : { px: 2, py: 1.5, mb: 1, width: "100%", display: "block" }
      }
    >
      <FormControl fullWidth size="small">
        <Select
          value={
            selectedTenantId === null ? "global" : selectedTenantId.toString()
          }
          onChange={handleTenantChange}
          displayEmpty
          disabled={loading}
          aria-label="Select tenant"
          inputProps={{ "data-testid": "tenant-select" }}
          sx={topbar ? topbarSelectSx : undefined}
          renderValue={(selected): ReactNode => {
            if (selected === "global" || selectedTenantId === null) {
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: THEME_CONSTANTS.borderRadius.round,
                      bgcolor: "var(--bg-sunken)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "none",
                      boxShadow: "none",
                    }}
                  >
                    <TenantIcon
                      sx={{
                        fontSize: 14,
                        color: "var(--ink-3)",
                      }}
                    />
                  </Box>
                  <Typography component="span" variant="body2">
                    Global
                  </Typography>
                </Box>
              );
            }

            const selectedValue =
              typeof selected === "string"
                ? selected
                : (selectedTenantId?.toString() ?? "");

            const tenant = tenants.find(
              (t) => t.tenantId.toString() === selectedValue,
            );

            if (!tenant) {
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: THEME_CONSTANTS.borderRadius.round,
                      bgcolor: "var(--bg-sunken)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "none",
                      boxShadow: "none",
                    }}
                  >
                    <TenantIcon
                      sx={{
                        fontSize: 14,
                        color: "var(--ink-3)",
                      }}
                    />
                  </Box>
                  <Typography component="span" variant="body2">
                    Global
                  </Typography>
                </Box>
              );
            }

            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TenantAvatar
                  tenantId={tenant.tenantId}
                  hasLogo={!!tenant.logo}
                  name={tenant.name}
                  logoUrl={logoUrlMap.get(tenant.tenantId)}
                />
                <Typography component="span" variant="body2">
                  {tenant.name}
                </Typography>
              </Box>
            );
          }}
        >
          <MenuItem value="global">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: THEME_CONSTANTS.borderRadius.round,
                  bgcolor: "var(--bg-sunken)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  boxShadow: "none",
                }}
              >
                <TenantIcon
                  sx={{
                    fontSize: 14,
                    color: "var(--ink-3)",
                  }}
                />
              </Box>
              <Typography variant="body2" fontWeight={500}>
                Global
              </Typography>
            </Box>
          </MenuItem>
          {tenants.map((tenant) => {
            return (
              <MenuItem
                key={tenant.tenantId}
                value={tenant.tenantId.toString()}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TenantAvatar
                    tenantId={tenant.tenantId}
                    hasLogo={!!tenant.logo}
                    name={tenant.name}
                    logoUrl={logoUrlMap.get(tenant.tenantId)}
                  />
                  <Typography variant="body2">{tenant.name}</Typography>
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
};

export default TenantDropdown;
